import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import { Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import { useAuth } from '@shared/providers/AuthContext';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import { useGlobalBehavior } from '@shared/providers/GlobalBehaviorWrapper';
import { useTenantSiteCheck } from '@/hooks/admin/useTenantSiteCheck';
import { LoginCredentials } from '@app-types/auth';
import { loginFormSchema, type LoginFormData } from '@/schemas/loginSchema';
import {
  AppButton,
  AppForm,
  AppSpin,
  AppTextField,
  CountdownErrorMessage,
} from '@/components/atoms';
import { Footer } from '@/components/organisms';
import { resolvePublicPortalProfile } from '@/features/sso/publicPortalProfile';
import {
  buildSsoLoginUrl,
  isSsoSite,
  markPassiveSsoCheck,
  selectPostLoginNavigation,
  shouldShowPassiveSsoSplash,
  shouldStartPassiveSsoCheck,
  stripPassiveMarkerFromUrl,
} from '@/features/sso/ssoSession';
import { rememberSsoIntendedPath, takeSsoIntendedPath } from '@/features/sso/activeSessionSync';

/**
 * Login Page Component
 *
 * Clinic user login page matching RA2 design.
 * Uses clinic_number + login_id + password authentication.
 */
const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { site } = useParams<{ site: string }>();
  const { colors } = useTheme();
  const { login, isLoading } = useAuth();
  const ssoEntryType = resolvePublicPortalProfile(site).mode === 'unified'
    ? 'unified'
    : 'clinic';
  const { setEnterKeyNavigationEnabled } = useGlobalBehavior();

  const {
    control,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
  } = useTrimmedForm<LoginFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(loginFormSchema),
    defaultValues: {
      clinic_number: '',
      login_id: '',
      password: '',
    },
  });

  useEffect(() => {
    setEnterKeyNavigationEnabled(false);
    return () => setEnterKeyNavigationEnabled(true);
  }, [setEnterKeyNavigationEnabled]);

  const {
    data: siteCheck,
    isLoading: isCheckingSite,
    isError: isSiteCheckError,
  } = useTenantSiteCheck(site);

  const validSite =
    !!site &&
    !isCheckingSite &&
    !isSiteCheckError &&
    !!siteCheck?.success &&
    !!siteCheck.data?.exists;

  const [passiveRedirectStarted, setPassiveRedirectStarted] = useState(false);
  // Captured ONCE -- same reason as SiteAdminLoginPage: stripping ?sso_passive=checked from the
  // URL must not flip this gate back to "not checked yet" and start another probe.
  const [ssoInitialSearch] = useState(() => location.search);
  const [intendedPath] = useState(() => {
    const from = (location.state as { from?: { pathname?: string; search?: string } } | null)?.from;
    return typeof from?.pathname === 'string'
      ? `${from.pathname}${from.search ?? ''}`
      : '';
  });
  // Same latch the admin lane already carries (SiteAdminLoginPage). WITHOUT IT the passive gate
  // re-arms on wall-clock alone -- shouldStartPassiveSsoCheck() compares Date.now() against the
  // sessionStorage marker -- and any render after that window flips it back on. Submitting is
  // itself such a render, so the effect below fired mid-submit, `location.assign` cancelled the
  // in-flight login request (nginx logs it 499) and the splash swapped the form out. The operator
  // saw the login screen again with no error, because nothing was ever rejected. QC hit exactly
  // this on P3 after an SLO; the same shape lives here.
  //
  // The ref is what the effect reads: state alone would need another render to take effect, and
  // the render that sets it is the same one that can fire the redirect.
  const manualLoginRef = useRef(false);
  const [manualLoginActive, setManualLoginActive] = useState(false);
  const activateManualLogin = () => {
    manualLoginRef.current = true;
    setManualLoginActive(true);
  };
  const canStartPassiveSsoCheck =
    isSsoSite(site) &&
    (isCheckingSite || validSite) &&
    shouldStartPassiveSsoCheck({
      context: { site, entryType: ssoEntryType },
      ssoEnabled: import.meta.env.VITE_SSO_ENABLED,
      passiveSsoEnabled: import.meta.env.VITE_SSO_PASSIVE_ENABLED,
      hasLocalCredential: Boolean(localStorage.getItem('auth_token')),
      manualLoginActive,
      search: ssoInitialSearch,
      storage: sessionStorage,
    });
  const showPassiveSsoSplash = shouldShowPassiveSsoSplash({
    canStartPassiveSsoCheck,
    passiveRedirectStarted,
  });

  const originalDestination = (location.state as { from?: Location })?.from?.pathname;

  useEffect(() => {
    if (
      !canStartPassiveSsoCheck ||
      passiveRedirectStarted ||
      manualLoginRef.current ||
      !isSsoSite(site)
    ) {
      return;
    }

    setPassiveRedirectStarted(true);
    // Same as the admin lane: the deep link the guard bounced lives in `state.from` and dies on
    // the whole-page redirect below, so write it down first.
    rememberSsoIntendedPath(
      { site, entryType: ssoEntryType },
      intendedPath,
      sessionStorage,
    );
    markPassiveSsoCheck(
      { site, entryType: ssoEntryType },
      sessionStorage,
    );
    const apiOrigin = import.meta.env.VITE_API_URL?.trim() || window.location.origin;
    window.location.assign(buildSsoLoginUrl(apiOrigin, {
      site,
      entryType: ssoEntryType,
      prompt: 'none',
    }));
  }, [canStartPassiveSsoCheck, passiveRedirectStarted, site, ssoEntryType]);

  // Strip a resolved `sso_passive=checked` so a later reload re-attempts passive.
  useEffect(() => {
    stripPassiveMarkerFromUrl();
  }, []);

  const onValid = async (data: LoginFormData) => {
    activateManualLogin();
    if (!validSite) return;

    clearErrors();

    const credentials: LoginCredentials = {
      clinic_number: data.clinic_number.trim(),
      login_id: data.login_id.trim(),
      password: data.password.trim(),
      site_type: site,
      authEntryType: ssoEntryType,
    };

    const result = await login(credentials);
    if (result.success) {
      const storedClinic = localStorage.getItem('auth_clinic');
      const clinicData = storedClinic ? JSON.parse(storedClinic) : null;
      const portalSiteType = clinicData?.parent_site_type || clinicData?.site_type || site || 'medix';
      // `originalDestination` comes from router state, which does NOT survive the passive SSO
      // check: that check is a whole-page redirect to the broker, so a visitor who opened a deep
      // link, got bounced through prompt=none, came back to this form and signed in locally
      // arrived with `history.state === null` and went to the portal root. Measured on dev1
      // 2026-09-03 for both tenants. The deep link is still in sessionStorage — the effect above
      // wrote it there for exactly this reason — so read it when router state has been wiped.
      const rememberedDestination = originalDestination
        ? undefined
        : takeSsoIntendedPath({ site, entryType: ssoEntryType }, sessionStorage);
      const redirectPath = originalDestination || rememberedDestination || `/${portalSiteType}`;
      const destination = selectPostLoginNavigation(result.ssoEstablishUrl, redirectPath);
      if (destination.kind === 'external') {
        window.location.replace(destination.url);
      } else {
        navigate(destination.url, { replace: true });
      }
      return;
    }

    setValue('password', '', { shouldValidate: false, shouldDirty: false });
    setError('password', { type: 'server', message: result.message });
  };

  const onInvalid = () => {
    setValue('password', '', { shouldValidate: false, shouldDirty: false });
  };

  if (showPassiveSsoSplash) {
    return <AppSpin fullscreen size="large" />;
  }

  if (site && !isCheckingSite && (!validSite || isSiteCheckError)) {
    return (
      <div className="flex min-h-screen flex-col overflow-x-hidden overflow-y-auto login-page-bg">
        <div className="flex flex-1 flex-col items-center justify-center px-4 py-6 sm:py-8">
          <div className="px-8 py-6 text-center bg-white rounded-lg shadow-lg bg-opacity-95">
            <h1 className="mb-4 text-xl font-bold text-red-600">無効なサイト</h1>
            <p className="text-gray-600">指定されたサイトは存在しません。</p>
            <AppButton
              type="primary"
              loading={isCheckingSite}
              onClick={() => navigate('/medix/login')}
              className="mt-4"
              style={{ backgroundColor: colors.primary }}
            >
              ログイン画面へ
            </AppButton>
          </div>
        </div>
        <div className="shrink-0">
          <Footer fixed={false} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col overflow-x-hidden overflow-y-auto login-page-bg">
      <div className="flex flex-1 flex-col items-center justify-center px-4 py-4 sm:py-8">
        <div className="w-full max-w-[460px] py-2 sm:-translate-y-4">
          <div className="my-4 sm:my-8 md:my-12">
            <img src="/images/login/logo.png" alt="RA2N Logo" className="w-full" />
          </div>

          <div className="px-8 py-6 bg-white rounded-lg shadow-lg bg-opacity-95">
            <form
              onFocusCapture={activateManualLogin}
              onSubmit={handleSubmit(onValid, onInvalid)}
              className="space-y-3"
            >
              <Controller
                name="clinic_number"
                control={control}
                render={({ field, fieldState }) => (
                  <AppForm.Item
                    validateStatus={fieldState.error ? 'error' : undefined}
                    help={fieldState.error?.message}
                    className="mb-0"
                  >
                    <AppTextField
                      {...field}
                      id="clinic_number"
                      placeholder="治療院ID"
                      className="w-full"
                      autoComplete="username"
                      autoFocus
                      data-testid="clinic-number-input"
                    />
                  </AppForm.Item>
                )}
              />

              <Controller
                name="login_id"
                control={control}
                render={({ field, fieldState }) => (
                  <AppForm.Item
                    validateStatus={fieldState.error ? 'error' : undefined}
                    help={fieldState.error?.message}
                    className="mb-0"
                  >
                    <AppTextField
                      {...field}
                      id="login_id"
                      placeholder="ユーザーID"
                      className="w-full"
                      autoComplete="username"
                      data-testid="login-id-input"
                    />
                  </AppForm.Item>
                )}
              />

              <Controller
                name="password"
                control={control}
                render={({ field, fieldState }) => {
                  const isFieldLevelError =
                    !!fieldState.error && fieldState.error.type !== 'server';
                  return (
                    <AppForm.Item
                      validateStatus={isFieldLevelError ? 'error' : undefined}
                      help={
                        fieldState.error?.message
                          ? (
                            <span className={isFieldLevelError ? undefined : 'text-red-500'}>
                              <CountdownErrorMessage message={fieldState.error.message} />
                            </span>
                          )
                          : undefined
                      }
                      className="mb-0"
                    >
                      <AppTextField
                        {...field}
                        id="password"
                        type="password"
                        placeholder="パスワード"
                        className="w-full"
                        autoComplete="current-password"
                        data-testid="password-input"
                      />
                    </AppForm.Item>
                  );
                }}
              />

              <div className="flex justify-center pt-3">
                <AppButton
                  type="primary"
                  htmlType="submit"
                  loading={isLoading}
                  disabled={isLoading}
                  className="px-8 py-2 transition-opacity duration-200 hover:opacity-90"
                  style={{ backgroundColor: colors.primary }}
                  data-testid="login-submit"
                >
                  {isLoading ? 'ログイン中...' : 'ログイン'}
                </AppButton>
              </div>
            </form>

            <div className="mt-4 text-center">
              <Link
                to={`/${site || 'medix'}/forgot-password`}
                className="text-sm hover:opacity-80"
                style={{ color: colors.primary }}
                data-testid="forgot-password-link"
              >
                パスワードを忘れた方はこちら
              </Link>
            </div>
          </div>
        </div>
      </div>
      <div className="shrink-0">
        <Footer fixed={false} />
      </div>
    </div>
  );
};

export default LoginPage;
